import { DateTime } from 'luxon';

// Mirrors the backend AssignmentTimeRangeContainsTripTimeRange constraint
// message template prefix in backend/apps/trips/constraints.py. Stable enough
// to match for reorder/detection without parsing the rest of the string.
const SCHEDULE_EXPLANATION_PREFIX = "This trip's pickup";

export function isScheduleExplanation(explanation: string): boolean {
  return explanation.startsWith(SCHEDULE_EXPLANATION_PREFIX);
}

// True when the schedule violation appears anywhere in the explanation
// list. Used from the standard invalid-assignment modal's override path to
// decide whether confirm should route through the reschedule modal — if
// no schedule violation is present, there's nothing to reschedule.
export function hasScheduleExplanation(explanations: string[]): boolean {
  return explanations.some(isScheduleExplanation);
}

// True when the schedule violation is the ONLY problem with the
// assignment. This is the gate for skipping the standard invalid-
// assignment modal and going straight to the reschedule modal — any
// other violation (including the technically-separate clocked-in /
// clocked-out constraints) needs to surface through the invalid modal
// so the dispatcher sees them before choosing to override. Rescheduling
// won't fix a driver who has already clocked out for the day.
export function isScheduleOnlyExplanation(explanations: string[]): boolean {
  return explanations.length === 1 && isScheduleExplanation(explanations[0]);
}

export function reorderScheduleExplanationLast(
  explanations: string[]
): string[] {
  const schedule: string[] = [];
  const others: string[] = [];
  for (const e of explanations) {
    (isScheduleExplanation(e) ? schedule : others).push(e);
  }
  return [...others, ...schedule];
}

// Callers use this to hide unrecoverable crews from the menu and silently
// reject out-of-shift drops — there's no pickup time that would keep the
// whole trip inside a shorter shift, so the reschedule modal can't help.
export function tripFitsInShift({
  pickupAt,
  dropoffAt,
  crewStart,
  crewEnd,
}: {
  pickupAt: string;
  dropoffAt: string;
  crewStart: string;
  crewEnd: string;
}): boolean {
  const pickup = DateTime.fromISO(pickupAt);
  const dropoff = DateTime.fromISO(dropoffAt);
  const start = DateTime.fromISO(crewStart);
  const end = DateTime.fromISO(crewEnd);
  return end.diff(start) >= dropoff.diff(pickup);
}

export function isCrewScheduleViolation({
  pickupAt,
  dropoffAt,
  crewStart,
  crewEnd,
}: {
  pickupAt: string;
  dropoffAt: string;
  crewStart: string;
  crewEnd: string;
}): boolean {
  const pickup = DateTime.fromISO(pickupAt);
  const dropoff = DateTime.fromISO(dropoffAt);
  const start = DateTime.fromISO(crewStart);
  const end = DateTime.fromISO(crewEnd);
  return pickup < start || dropoff > end;
}

// Latest pickup that keeps the whole trip inside the crew's shift, capped
// at the original pickup so we never push the dispatcher later than they
// intended. Clamped to crewStart when the original pickup precedes the
// shift. Returns null when no pickup fits — the caller falls back to the
// standard invalid-assignment modal so the dispatcher isn't prompted with
// a time that will fail validation again.
export function suggestedPickupTimeWithinCrewSchedule({
  pickupAt,
  dropoffAt,
  crewStart,
  crewEnd,
}: {
  pickupAt: string;
  dropoffAt: string;
  crewStart: string;
  crewEnd: string;
}): string | null {
  const pickup = DateTime.fromISO(pickupAt);
  const dropoff = DateTime.fromISO(dropoffAt);
  const start = DateTime.fromISO(crewStart);
  const end = DateTime.fromISO(crewEnd);
  const duration = dropoff.diff(pickup);

  if (end.diff(start) < duration) return null;

  if (pickup < start) {
    return start.toISO();
  }
  const latestValidPickup = end.minus(duration);
  return DateTime.min(latestValidPickup, pickup).toISO();
}
